/*
 * Earbuds Restore G1e finder intake.
 * Workflow state is local presentation state only; every route stops at UNKNOWN.
 *
 * 15-09-2026: de bladerknop vertelt nu waar hij heen gaat. Hij filterde al op het gekozen
 * onderdeel, maar bleef "Bekijk assortiment" heten, waardoor het voelde alsof je keuze werd
 * genegeerd en je naar het hele assortiment werd gestuurd. Label en href lopen nu gelijk op.
 *
 * 15-09-2026 (middag): een gekozen onderdeel wordt ook als gebeurtenis er:finder-part uitgezonden. De
 * directe bestelbox op de homepage (sections/er-home-buybox) vult daarmee stap "Wat mis je?" in, zodat
 * de klant zonder paginawissel verder kiest. Er wordt niets gekocht of vooraf aangevinkt buiten die stap.
 */

const ALLOWED_PARTS = new Set(['earbud_L', 'earbud_R', 'case']);

const BROWSE_LABELS = {
  nl: {
    earbud_L: 'Bekijk linker oortjes',
    case: 'Bekijk oplaadcases',
    earbud_R: 'Bekijk rechter oortjes',
  },
  en: {
    earbud_L: 'View left earbuds',
    case: 'View charging cases',
    earbud_R: 'View right earbuds',
  },
  fr: {
    earbud_L: 'Voir les écouteurs gauches',
    case: 'Voir les boîtiers de charge',
    earbud_R: 'Voir les écouteurs droits',
  },
};

function browseLabelsForPage() {
  const taal = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
  return BROWSE_LABELS[taal] || BROWSE_LABELS.nl;
}

class EarbudsRestoreFinderIntake extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    this.partPanel = this.querySelector('[data-er-finder-panel="part"]') || this;
    this.unknownPanel = this.querySelector('[data-er-finder-panel="unknown"]');
    this.statusOutput = this.querySelector('[data-er-finder-status]');
    this.continueButton = this.querySelector('[data-er-finder-continue]');
    this.backButton = this.querySelector('[data-er-finder-back]');
    this.resetButton = this.querySelector('[data-er-finder-reset]');
    this.unknownHeading = this.querySelector('[data-er-finder-unknown-heading]');
    this.helpLink = this.dataset.mode === 'hero'
      ? this.closest('section[data-er-section-id]')?.querySelector('.er-finder__home-help > a.er-finder__help-link')
      : null;
    this.helpHref = this.helpLink?.getAttribute('href') || '';
    this.browseLink = this.dataset.mode === 'hero' && this.dataset.nativeProfile === 'native-advisory-v1'
      ? this.closest('section[data-er-section-id]')?.querySelector('[data-er-native-browse]')
      : null;
    this.browseHref = this.browseLink?.getAttribute('href') || '';
    this.browseLabelDefault = this.browseLink ? this.browseLink.textContent.trim() : '';
    this.browseLabels = browseLabelsForPage();

    this.addEventListener('change', (event) => this.handlePartChange(event), { signal });
    this.continueButton?.addEventListener('click', () => this.continue(), { signal });
    this.backButton?.addEventListener('click', () => this.back(), { signal });
    this.resetButton?.addEventListener('click', () => this.reset(), { signal });
    this.reset();
  }

  disconnectedCallback() {
    this.syncHelpPart();
    this.syncBrowsePart();
    this.abortController?.abort();
    this.abortController = null;
  }

  partInputs() {
    return [...this.querySelectorAll('input[type="radio"]')];
  }

  selectedPart() {
    const selected = this.partInputs().find((input) => input.checked);
    return selected && ALLOWED_PARTS.has(selected.value) ? selected.value : '';
  }

  setStatus(message) {
    if (this.statusOutput) this.statusOutput.textContent = message || this.dataset.labelPartRequired || '';
  }

  syncHelpPart(part = '') {
    if (!this.helpLink || !/^\/(?!\/)/.test(this.helpHref) || /[#\\]/.test(this.helpHref)) return;
    this.helpLink.setAttribute('href', ALLOWED_PARTS.has(part) ? `${this.helpHref}#er-part=${part}` : this.helpHref);
  }

  syncBrowsePart(part = '') {
    if (!this.browseLink || !/^\/(?!\/)/.test(this.browseHref) || /[?#\\]/.test(this.browseHref)) return;
    const filterValues = { earbud_L: 'Linker', case: 'Oplaadcase', earbud_R: 'Rechter' };
    const gekozen = ALLOWED_PARTS.has(part);
    this.browseLink.setAttribute('href', gekozen
      ? `${this.browseHref}?filter.p.m.custom.airpods_type=${encodeURIComponent(filterValues[part])}`
      : this.browseHref);
    const label = gekozen ? this.browseLabels[part] : '';
    if (label || this.browseLabelDefault) {
      this.browseLink.textContent = label || this.browseLabelDefault;
    }
  }

  showPartPanel() {
    if (this.partPanel !== this) this.partPanel.hidden = false;
    if (this.unknownPanel) this.unknownPanel.hidden = true;
  }

  handlePartChange(event) {
    if (!this.partInputs().includes(event.target)) return;

    const selectedPart = this.selectedPart();
    if (!selectedPart) {
      this.reset();
      return;
    }

    this.dataset.state = 'PART_SELECTED';
    this.dataset.selectedPart = selectedPart;
    if (this.continueButton) this.continueButton.disabled = false;
    this.syncHelpPart(selectedPart);
    this.syncBrowsePart(selectedPart);
    document.dispatchEvent(new CustomEvent('er:finder-part', { detail: { part: selectedPart } }));
    this.setStatus(
      this.dataset.mode === 'hero' ? this.dataset.labelHeroSelected : this.dataset.labelPartSelected,
    );
  }

  continue() {
    const selectedPart = this.selectedPart();
    if (!selectedPart || !this.unknownPanel) {
      this.reset();
      return;
    }

    this.dataset.state = 'IDENTIFICATION_REQUIRED_UNKNOWN';
    this.dataset.selectedPart = selectedPart;
    if (this.partPanel !== this) this.partPanel.hidden = true;
    this.unknownPanel.hidden = false;
    this.setStatus(this.dataset.labelUnknown);
    this.unknownHeading?.focus();
  }

  back() {
    this.showPartPanel();
    const selectedPart = this.selectedPart();
    if (!selectedPart) {
      this.reset();
      return;
    }

    this.dataset.state = 'PART_SELECTED';
    this.dataset.selectedPart = selectedPart;
    if (this.continueButton) this.continueButton.disabled = false;
    this.setStatus(this.dataset.labelPartSelected);
  }

  reset() {
    for (const input of this.partInputs()) input.checked = false;
    this.dataset.state = 'PART_REQUIRED';
    delete this.dataset.selectedPart;
    this.syncHelpPart();
    this.syncBrowsePart();
    if (this.continueButton) this.continueButton.disabled = true;
    this.showPartPanel();
    this.setStatus(this.dataset.labelPartRequired);
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('er-finder-intake')) {
  customElements.define('er-finder-intake', EarbudsRestoreFinderIntake);
}
